import { useState } from 'react';
import { 
  FacebookLogo,
  XLogo,
  MessengerLogo,
  LinkedinLogo,
  EnvelopeSimple,
  Link,
  Check
} from '@phosphor-icons/react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

export interface ShareBarProps {
  /** Full URL to share */
  url: string;
  /** Article title */
  title: string;
  /** Optional variant - compact shows icons only */
  variant?: 'compact' | 'full';
  /** Optional className */
  className?: string;
}

/**
 * molecule-share-bar
 * 
 * Social sharing buttons for articles.
 * Supports Facebook, Messenger, LinkedIn, X (Twitter), Email, and Copy Link.
 */
export function ShareBar({ url, title, variant = 'compact', className }: ShareBarProps) {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    messenger: `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=0&redirect_uri=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      // ⚠️ ANALYTICS: Track copy link
      trackEvent(ANALYTICS_EVENTS.ARTICLE_SHARED, {
        share_platform: 'copy_link',
        share_url: url,
        article_title: title,
        share_method: 'clipboard',
      });
      toast({
        description: "Link copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        description: "Failed to copy link",
        variant: "destructive",
      });
    }
  };

  const handleShare = (platform: keyof typeof shareLinks) => {
    // ⚠️ ANALYTICS: Track share button click
    trackEvent(ANALYTICS_EVENTS.ARTICLE_SHARED, {
      share_platform: platform,
      share_url: url,
      article_title: title,
      share_method: 'social',
    });
    window.open(shareLinks[platform], '_blank', 'noopener,noreferrer,width=600,height=400');
  };

  const iconClass = "h-5 w-5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer";

  return (
    <div className={cn("flex items-center gap-4", className)}>
      {variant === 'full' && (
        <span className="text-sm text-muted-foreground mr-1">Share</span>
      )}
      
      <FacebookLogo
        weight="regular"
        className={iconClass}
        onClick={() => handleShare('facebook')}
        aria-label="Share on Facebook"
      />

      <XLogo
        weight="regular"
        className={iconClass}
        onClick={() => handleShare('twitter')}
        aria-label="Share on X"
      />

      <MessengerLogo
        weight="regular"
        className={iconClass}
        onClick={() => handleShare('messenger')}
        aria-label="Share on Messenger"
      />

      <LinkedinLogo
        weight="regular"
        className={iconClass}
        onClick={() => handleShare('linkedin')}
        aria-label="Share on LinkedIn"
      />

      <EnvelopeSimple
        weight="regular"
        className={iconClass}
        onClick={() => handleShare('email')}
        aria-label="Share via Email"
      />

      {copied ? (
        <Check className="h-5 w-5 text-green-600" />
      ) : (
        <Link
          weight="regular"
          className={iconClass}
          onClick={handleCopyLink}
          aria-label="Copy link"
        />
      )}
    </div>
  );
}

export default ShareBar;
